/**
 * Recognizer for inbound cross-app navigation paths — the Einstein routes a peer
 * (Galileo) may drive this app to. Parse-don't-validate at the boundary: an
 * intent whose path is not a known Einstein route is dropped rather than fed to
 * the router, so a malformed or future-versioned path can never push the app into
 * an unexpected state. Mirrors Galileo's `isRoutePath`.
 *
 * The recognized set is intentionally the deep-linkable, entity-scoped routes
 * (see `ExperienceShell`'s route table) — the surfaces another app has a reason
 * to open. Admin routes are deliberately excluded from cross-app targeting.
 */

// `/entities/:entityType/:entityId` — the work-items deep link (e.g.
// `/entities/load/L39005`). Both segments must be present and non-empty.
const ENTITY_PATH = /^\/entities\/[^/]+\/[^/]+$/;

// `/workflows` and `/workflows/:id`.
const WORKFLOW_PATH = /^\/workflows(?:\/[^/]+)?$/;

/**
 * Whether `path` is a known, deep-linkable Einstein route. Accepts an
 * origin-relative path (leading slash); query/hash are ignored for matching so a
 * peer may append them without breaking recognition.
 */
export function isEinsteinRoutePath(path: string): boolean {
    if (typeof path !== "string" || !path.startsWith("/")) {
        return false;
    }
    // First segment before any query/hash. split(limit) always yields at least
    // one element, but noUncheckedIndexedAccess types it as possibly-undefined,
    // so fall back to the whole path (unreachable in practice).
    const pathname = path.split(/[?#]/, 1)[0] ?? path;
    return ENTITY_PATH.test(pathname) || WORKFLOW_PATH.test(pathname);
}
